import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, CheckCircle2 } from 'lucide-react';
import { formatINR } from '@vj/shared';
import { api, ApiClientError } from '../../lib/api.js';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import { Button } from '../../components/ui/Button.jsx';
import { TextField, SelectField, TextAreaField } from '../../components/ui/Field.jsx';

const mobilePattern = /^[6-9]\d{9}$/;

function today() {
  return new Date().toISOString().slice(0, 10);
}

export default function AccountForm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [schemeId, setSchemeId] = useState('');
  const [customer, setCustomer] = useState({ id: null, name: '', mobile: '' });
  const [monthlyAmount, setMonthlyAmount] = useState('');
  const [joiningDate, setJoiningDate] = useState(today());
  const [notes, setNotes] = useState('');
  const [submitError, setSubmitError] = useState(null);

  const schemesQuery = useQuery({
    queryKey: ['chit', 'schemes', 'active'],
    queryFn: () => api.get('/chit/schemes', { query: { active: true } }),
  });
  const scheme = (schemesQuery.data ?? []).find((s) => String(s.id) === schemeId);

  useEffect(() => {
    if (scheme && !monthlyAmount) setMonthlyAmount(scheme.min_monthly);
  }, [scheme, monthlyAmount]);

  const debouncedMobile = useDebouncedValue(customer.mobile, 400);
  const canLookup = mobilePattern.test(debouncedMobile) && !customer.id;
  const lookupQuery = useQuery({
    queryKey: ['customers', 'by-mobile', debouncedMobile],
    queryFn: () => api.get(`/customers/by-mobile/${debouncedMobile}`),
    enabled: canLookup,
  });
  const matches = canLookup ? lookupQuery.data ?? [] : [];

  const selectCustomer = (c) => setCustomer({ id: c.id, name: c.name, mobile: c.mobile ?? '' });
  const clearCustomerMatch = () => setCustomer((c) => ({ ...c, id: null }));

  const createMutation = useMutation({
    mutationFn: (payload) => api.post('/chit/accounts', payload),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['chit', 'accounts'] });
      navigate(`/chit/${result.id}`, { state: { justCreated: true } });
    },
    onError: (err) => setSubmitError(err instanceof ApiClientError ? err.message : 'Could not open this account. Please try again.'),
  });

  const canSubmit =
    !!schemeId &&
    customer.name.trim().length >= 2 &&
    Number(monthlyAmount) > 0 &&
    (!scheme || Number(monthlyAmount) >= Number(scheme.min_monthly));

  const onSubmit = (e) => {
    e.preventDefault();
    setSubmitError(null);
    createMutation.mutate({
      schemeId: Number(schemeId),
      customerId: customer.id ?? undefined,
      customerName: customer.name,
      customerMobile: customer.mobile || undefined,
      monthlyAmount: Number(monthlyAmount),
      joiningDate,
      notes: notes || undefined,
    });
  };

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-5 pb-28">
      <h1 className="font-display text-2xl font-semibold tracking-tight">New chit account</h1>

      <section className="card flex flex-col gap-4 p-5">
        <SelectField label="Scheme" required value={schemeId} onChange={(e) => setSchemeId(e.target.value)}>
          <option value="">Select a scheme</option>
          {(schemesQuery.data ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.name} — {s.scheme_type === 'gold' ? 'gold' : 'rupee'}, {s.duration_months} months
            </option>
          ))}
        </SelectField>
        {scheme ? (
          <p className="text-sm text-muted">
            {scheme.scheme_type === 'gold'
              ? 'Each payment credits grams at that day’s rate; the account holds the accrued weight.'
              : scheme.bonus_type === 'none'
                ? 'No maturity bonus on this plan.'
                : scheme.bonus_type === 'installments'
                  ? `Matures with ${scheme.bonus_value} free installment(s) added as a bonus.`
                  : `Matures with a ${scheme.bonus_value}% bonus on the total paid.`}
            {Number(scheme.min_monthly) > 0 ? ` Minimum ${formatINR(scheme.min_monthly)}/month.` : ''}
          </p>
        ) : null}
      </section>

      <section className="card flex flex-col gap-4 p-5">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Customer</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="relative">
            <TextField
              label="Mobile"
              inputMode="numeric"
              value={customer.mobile}
              onChange={(e) => setCustomer((c) => ({ ...c, mobile: e.target.value, id: c.id && e.target.value !== c.mobile ? null : c.id }))}
              hint={customer.id ? 'Matched to a saved customer' : 'Type 10 digits to search'}
            />
            {matches.length > 0 ? (
              <div className="absolute z-10 mt-1 w-full rounded-card border border-border bg-surface shadow-lg">
                {matches.map((m) => (
                  <button key={m.id} type="button" onClick={() => selectCustomer(m)} className="flex w-full items-center gap-2 px-3.5 py-2.5 text-left text-sm hover:bg-surface-2">
                    <Search size={14} className="text-muted" aria-hidden="true" />
                    <span className="font-medium">{m.name}</span>
                    <span className="text-muted">{m.city}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <TextField label="Name" required value={customer.name} onChange={(e) => setCustomer((c) => ({ ...c, name: e.target.value }))} />
        </div>
        {customer.id ? (
          <button type="button" onClick={clearCustomerMatch} className="self-start text-sm text-primary hover:underline">
            Not this customer — enter details manually
          </button>
        ) : null}
      </section>

      <section className="card grid grid-cols-1 gap-3 p-5 sm:grid-cols-2">
        <TextField label="Monthly amount (₹)" type="number" inputMode="decimal" required value={monthlyAmount} onChange={(e) => setMonthlyAmount(e.target.value)} />
        <TextField label="Joining date" type="date" required value={joiningDate} onChange={(e) => setJoiningDate(e.target.value)} />
      </section>

      <TextAreaField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />

      {submitError ? (
        <p role="alert" className="rounded-card border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
          {submitError}
        </p>
      ) : null}

      <div className="raised fixed inset-x-0 bottom-0 z-20 flex items-center justify-end gap-2 border-t border-border bg-surface px-4 py-3 shadow-raised sm:static sm:border-0 sm:bg-transparent sm:p-0 sm:shadow-none">
        <Button type="submit" size="lg" disabled={!canSubmit} loading={createMutation.isPending}>
          <CheckCircle2 size={18} aria-hidden="true" /> Open account
        </Button>
      </div>
    </form>
  );
}
